/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Component Select
 * Get property:
 * options - list of value
 * selectedOption - default value
 * kind - type of select
 * onSelect - executable callback
 * width - width of select-block
 *
 * @param {options: IOptionItemProps[], selectedOption: IOptionItemProps, kind?: string, onSelect: Function}
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Ilya Babnov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */
import { Fragment, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, ReactElement } from 'react';

import { icons } from '@assets/index';
import initTranslationCommon from '@modules/internationalization/constants/common';
import type ICommonTranslation from '@modules/internationalization/types/common';
import { Loader } from '@modules/loader/components/Loader';

import { DropDownFilterButtonSize } from '../DropDownFilter/DropDownFilter';
import { DropDownFilter, Input } from '../index';
import { EInputKind, EInputType } from '../Input/Input';
import SelectList from './components/SelectList';
import { SelectContainerStyled } from './styled/SelectStyled';

export interface IOptionItemProps {
    value: string | number;
    label: string;
    labelIcon?: ReactElement | null;
    disabled?: boolean;
    dataName?: string;
}

export interface ISelectProps {
    onOpenAndClose?: (isOpen: boolean) => void;
    classNamePrefix?: string;
    dataName?: string;
    disabled?: boolean;
    filterButtonSize?: DropDownFilterButtonSize;
    icon?: string;
    kind: string;
    onCloseClick?: () => void;
    onSelect: (selectedOption: IOptionItemProps) => void;
    options: IOptionItemProps[];
    placeholder: string;
    renderButton?: () => ReactElement;
    renderInBody?: boolean;
    searchProps?: ISelectSearchProps;
    selectedOption: IOptionItemProps;
    translation: ICommonTranslation;
    virtualListProps?: ISelectVirtualListProps;
    widthAuto?: boolean;
    maxWidth?: string;
    instructionLinkRef?: RefObject<HTMLAnchorElement>;
}

export interface ISelectSearchProps {
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    searchValue?: string;
}

export interface ISelectVirtualListProps {
    rowHeight: number;
    hasMore: boolean;
    isLoading: boolean;
    onScrollEvent: (searchValue: string) => void;
}

const defaultProps: Partial<ISelectProps> = {
    kind: 'normal',
    onSelect: () => {},
    options: [],
    placeholder: '',
    selectedOption: {
        label: '',
        value: '',
    },
    translation: initTranslationCommon,
};

const Select = (props: ISelectProps) => {
    const [searchValue, setSearchValue] = useState('');
    const refSearchString = useRef('');

    const {
        classNamePrefix,
        maxWidth,
        options,
        placeholder,
        selectedOption,
        translation,
        searchProps,
        virtualListProps,
        onSelect,
        icon,
        instructionLinkRef,
        ...rest
    } = props;
    // generate className for main container
    const selectClassName = ['select', classNamePrefix ? `${classNamePrefix}select` : ''].join(' ');
    /**
     * @description Select item and close list
     * @param selectedOption
     */
    const selectItem = (selectedOption: IOptionItemProps): void => {
        onSelect(selectedOption);
    };

    const handleSearchChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (searchProps?.onSearchChange) {
                searchProps.onSearchChange(e.target.value);
                // TODO если управляемый элемент не обновлять внутренний стейт SearchValue
                // return
            }
            setSearchValue(() => {
                refSearchString.current = e.target.value;
                return e.target.value;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchProps?.onSearchChange],
    );

    const listProps = useMemo(() => {
        return virtualListProps
            ? {
                  ...virtualListProps,
                  onScrollEvent: () => virtualListProps?.onScrollEvent(refSearchString.current),
              }
            : undefined;
    }, [virtualListProps]);

    return (
        <DropDownFilter
            {...rest}
            filterButtonLabel={selectedOption.label}
            icon={
                icon
                    ? icon
                    : rest.filterButtonSize === DropDownFilterButtonSize.medium
                    ? icons.arrowListDownMedium
                    : icons.arrowListDownSmall
            }
            instructionLinkRef={instructionLinkRef}
            render={() => (
                <SelectContainerStyled
                    $isLoading={virtualListProps?.isLoading}
                    className={selectClassName}
                    kind={rest.kind}
                    maxWidth={maxWidth}
                />
            )}
            translation={{ ...translation.dropDown, placeholder }}
            onSelect={selectItem}
        >
            {virtualListProps?.isLoading && <Loader backdropOpacity={0.7} showLoader={true} />}
            {searchProps && (
                // Fragment prevents Warning 'not recognize prop on DOM element'
                <Fragment>
                    <Input
                        autoFocus={true}
                        className="select__search-input"
                        data-name="selectSearch"
                        kind={EInputKind.normal}
                        placeholder={searchProps.searchPlaceholder ?? translation.select.searchPlaceholder}
                        righticon={icons.searchSmallIcon}
                        type={EInputType.small}
                        value={searchProps.searchValue ?? searchValue}
                        onChange={handleSearchChange}
                        onEnter={() => {}}
                    />
                </Fragment>
            )}
            <SelectList options={options} selectedOption={selectedOption} virtualListProps={listProps} />
        </DropDownFilter>
    );
};

Select.defaultProps = defaultProps;

export default Select;
